/**
 * BeeEntity - Concrete implementation of a bee enemy
 *
 * Bees move in a single direction (horizontal or vertical) and bounce
 * off walls. They move every 500ms.
 */

import { BaseTile, MoveContext, MoveResult, TextureDescriptor } from "./BaseTile";
import { TILE, isEmpty } from "../constants/gameConstants";
import { Coordinates, TileAndEntity, TileIndex } from "../types";

import { Direction } from "../utils/Direction";

export class BeeEntity extends BaseTile {
  // Bee-specific metadata
  orientation: "horizontal" | "vertical";
  direction: Direction; // Current movement direction
  timeSinceLastMove: number; // Accumulator in milliseconds
  moveInterval: number = 500; // Move every 500ms

  constructor({
    row,
    col,
    orientation,
    id,
  }: {
    row: number;
    col: number;
    orientation: "horizontal" | "vertical";
    id?: string;
  }) {
    // Generate unique ID if not provided
    super(id ?? `bee-${col}-${row}-${Date.now()}`, col, row);

    this.orientation = orientation;
    this.timeSinceLastMove = 0;

    // Set initial direction based on orientation
    if (orientation === "horizontal") {
      this.direction = new Direction("right");
    } else {
      this.direction = new Direction("down");
    }
  }

  /**
   * Get tile type based on orientation
   */
  tileType(): TileIndex {
    return this.orientation === "horizontal"
      ? TILE.BEE_HORIZONTAL
      : TILE.BEE_VERTICAL;
  }

  /**
   * Get texture descriptor based on orientation
   */
  textureDescriptor(): TextureDescriptor {
    const path = this.orientation === "horizontal"
      ? "tiles/bee-horizontal.png"
      : "tiles/bee-vertical.png";
    return { type: "png", path };
  }

  isEntity(): boolean {
    return true; // Bee is a movable entity
  }

  move(context: MoveContext): MoveResult {
    // Accumulate time
    this.timeSinceLastMove += context.deltaMs;

    // Not ready to move yet?
    if (this.timeSinceLastMove < this.moveInterval) {
      return { type: "wait" };
    }

    // Reset timer
    this.timeSinceLastMove = 0;

    // Try to move in current direction
    const target = context.getAt(this.nextPosition());
    const currentTile = context.getAt(this.coordinates()).tile;
    // todo: better logic needed for whenever someone moves off a button
    // todo: also all the enemies have a lot of shared logic.
    const maybeReleaseButton = () => { if (currentTile === TILE.BUTTON) { context.globalState.releaseButton(); } }
    // Check for bomb collision (both explode)
    if (target.tile === TILE.BOMB) {
      return { type: "die", who: "both", collisionWith: "tile", direction: this.direction };
    } else if (target.entity === TILE.PLAYER_START) {
      return { type: "die", collisionWith: "entity", who: "both", direction: this.direction };
    } else if (target.tile === TILE.BUTTON) {
      maybeReleaseButton();
      return { type: "pressButton", direction: this.direction };
    }

    // Check if target is passable
    if (this.canMoveTo(target)) {
      maybeReleaseButton();
      return { type: "move", direction: this.direction };
    } else {
      // Hit wall, reverse direction
      this.reverseDirection();
      return { type: "wall" };
    }
  }

  reverseDirection(): void {
    if (this.orientation === "horizontal") {
      this.direction = this.direction.value === "right" ? new Direction("left") : new Direction("right");
    } else {
      this.direction = this.direction.value === "down" ? new Direction("up") : new Direction("down");
    }
  }

  nextPosition(): Coordinates {
    if (this.orientation === "horizontal") {
      return {
        col: this.col + (this.direction.value === "right" ? 1 : -1),
        row: this.row,
      };
    } else {
      return {
        col: this.col,
        row: this.row + (this.direction.value === "down" ? 1 : -1),
      };
    }
  }

  /**
   * Check if bee can move to the target position
   */
  private canMoveTo(target: TileAndEntity): boolean {
    const { tile, entity } = target;
    console.log("BeeEntity.canMoveTo:", target);
    // Query functions return null for out-of-bounds positions
    if (tile === null) {
      return false;
    }

    const actAsWalls: TileIndex[] = [
      TILE.WALL,
      TILE.DOOR_GREEN,
      TILE.DOOR_ORANGE,
      TILE.DOOR_RED,
      TILE.DOOR_BLUE,
      TILE.BUTTON_DOOR,
      TILE.EXIT,
      TILE.SAND,
      TILE.WATER,
      TILE.ARROW_UP,
      TILE.ARROW_DOWN,
      TILE.ARROW_LEFT,
      TILE.ARROW_RIGHT,
      TILE.STAR,
      TILE.SUCTION_BOOTS,
      TILE.FLIPPERS,
      TILE.KEY_BLUE,
      TILE.KEY_RED,
      TILE.KEY_GREEN,
      TILE.KEY_ORANGE,
    ];

    if (actAsWalls.includes(tile)) {
      return false;
    }

    if (entity === TILE.PLAYER_START) {
      return true;
    }

    // Check entity layer - any entity blocks
    if (!isEmpty(entity)) {
      return false;
    }

    // Otherwise passable
    return true;
  }

  clone(): BeeEntity {
    const copy = new BeeEntity({
      col: this.col,
      row: this.row,
      orientation: this.orientation,
      id: this.id,
    });
    copy.direction = this.direction;
    copy.timeSinceLastMove = this.timeSinceLastMove;
    copy.moveInterval = this.moveInterval;
    copy.sprite = this.sprite; // Keep reference to same sprite
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "bee",
      id: this.id,
      col: this.col,
      row: this.row,
      orientation: this.orientation,
      direction: this.direction,
      timeSinceLastMove: this.timeSinceLastMove,
    };
  }
}
